import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'src/client/js/view/components/Grid';
import Button from 'wonderbly-components/lib/Button';
import Select from 'wonderbly-components/lib/Select';
import InlineFormField from 'wonderbly-components/lib/InlineFormField';
import AddressList from './AddressList';
import Tooltip from 'wonderbly-components/lib/Tooltip';
import BingMap from './BingMap';

import './HomeAddress.scss';

const HomeAddress = props =>
  props.isLatLngComplete ? (
    <BingMap
      setSubmitting={props.setSubmitting}
      setAddressListIndex={props.setAddressListIndex}
      cta={props.drawer.saveCta}
      editAddress={props.drawer.editAddress}
    />
  ) : (
    <form className="rows-spaced home-address" onSubmit={props.handleSubmit} autoComplete="new-password">
      <Row
        colsSpacedOn="base"
        colsToRowsOn="fablet-down"
        rowsSpacedOn="base"
        className="rows-spaced-big-on-fablet-down">
        <Col className="col-1-2 rows-spaced">
          <div className="row">
            <span className="home-address-label">
              {props.drawer.countryTitle}
            </span>
            <Select
              id="country"
              options={props.countryOptions}
              value={props.values.country}
              onChange={props.onChange}
            />
          </div>
          <Row colsSpacedOn="base" className="home-address-row">
            <div className="home-address-label">
              {props.drawer.addressTitle}
            </div>
            <Row
              colsSpacedOn="base"
              colsToRowsOn="fablet-down"
              rowsSpacedOn="base">
              <Col className="col-2-5">
                <div className="home-address-input-wrapper">
                  <Tooltip className="error-tooltip" visible={props.errors && !!props.errors.door} absolute={!props.media.fabletDown} type="alert">
                    {props.errors && props.errors.door}
                  </Tooltip>
                  <InlineFormField
                    id="door"
                    label={
                      <span>
                        {props.drawer.doorLabel}
                        <span className="text-milli color-red">*</span>
                      </span>
                    }
                    placeholder={props.drawer.doorLabel}
                    fieldType="input"
                    value={props.values.door}
                    onChange={props.onChange}
                  />
                </div>
              </Col>
              <Col className="col-3-5">
                <div className="home-address-input-wrapper">
                  <Tooltip className="error-tooltip" visible={props.errors && !!props.errors.street} absolute={!props.media.fabletDown} type="alert">
                    {props.errors && props.errors.street}
                  </Tooltip>
                  <InlineFormField
                    id="street"
                    label={
                      <span>
                        {props.drawer.streetLabel}
                        <span className="text-milli color-red">*</span>
                      </span>
                    }
                    placeholder={props.drawer.streetLabel}
                    fieldType="input"
                    value={props.values.street}
                    onChange={props.onChange}
                  />
                </div>
              </Col>
            </Row>
            <Row
              className="home-address-row"
              colsSpacedOn="base"
              rowsSpacedOn="base"
              colsToRowsOn="fablet-down">
              <Col className="col-3-5">
                <div className="home-address-input-wrapper">
                  <Tooltip className="error-tooltip" visible={props.errors && !!props.errors.city} absolute={!props.media.fabletDown} type="alert">
                    {props.errors && props.errors.city}
                  </Tooltip>
                  <InlineFormField
                    id="city"
                    label={
                      <span>
                        {props.drawer.townLabel}
                        <span className="text-milli color-red">*</span>
                      </span>
                    }
                    placeholder={props.drawer.townLabel}
                    fieldType="input"
                    value={props.values.city}
                    onChange={props.onChange}
                  />
                </div>
              </Col>
              <Col className="col-2-5">
                <InlineFormField
                  id="postcode"
                  label={props.drawer.postcodeLabel}
                  placeholder={props.drawer.postcodeLabel}
                  fieldType="input"
                  value={props.values.postcode}
                  onChange={props.onChange}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <div className="color-red home-address-smallprint">
                  {props.drawer.inBookReminder}
                </div>
              </Col>
            </Row>
          </Row>
        </Col>
        <Col className="col-1-2">
          <span className="home-address-label">
            {props.drawer.addressListTitle}
          </span>
          <AddressList
            addresses={props.addresses}
            addressListIndex={props.addressListIndex}
            onChange={props.handleAddressListChange}
          />
          <div className="home-address-button">
            <Button
              type="submit"
              colour="green"
              className="widest-on-fablet-down"
              disabled={props.isSubmitting || props.addressListIndex === null}
              raised>
              {props.drawer.continueCta}
            </Button>
          </div>
        </Col>
      </Row>
    </form>
  );

HomeAddress.propTypes = {
  queryParams: PropTypes.shape({
    door: PropTypes.string,
    street: PropTypes.string,
    city: PropTypes.string,
    postcode: PropTypes.string,
    country: PropTypes.string,
  }),
  values: PropTypes.shape({
    door: PropTypes.string.isRequired,
    street: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    postcode: PropTypes.string,
    country: PropTypes.string.isRequired,
  }),
  errors: PropTypes.shape({
    door: PropTypes.string,
    street: PropTypes.string,
    city: PropTypes.string,
  }),
  countryOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ),
  drawer: PropTypes.shape({
    saveCta: PropTypes.string.isRequired,
    editAddress: PropTypes.string.isRequired,
    countryTitle: PropTypes.string.isRequired,
    addressTitle: PropTypes.string.isRequired,
    doorLabel: PropTypes.string.isRequired,
    streetLabel: PropTypes.string.isRequired,
    townLabel: PropTypes.string.isRequired,
    postcodeLabel: PropTypes.string.isRequired,
    inBookReminder: PropTypes.string.isRequired,
    addressListTitle: PropTypes.string.isRequired,
    continueCta: PropTypes.string.isRequired,
  }),
  isLatLngComplete: PropTypes.bool.isRequired,
  isAddressFilled: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  addressListIndex: PropTypes.number,
  setAddressListIndex: PropTypes.func.isRequired,
  handleAddressListChange: PropTypes.func.isRequired,
  addresses: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  media: PropTypes.shape({
    fabletDown: PropTypes.bool.isRequired,
  }),
  setSubmitting: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};

export default HomeAddress;
